import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { Row, Button, Form, Typography, Icon } from 'antd'
import { Formik } from 'formik'
import * as yup from 'yup'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser'

import { Creators as authActions } from '../../appRedux/ducks/auth/auth'
import { Creators as AlertActions } from '../../appRedux/ducks/alert'
import logo from '../../assets/images/Logo@2x.png'
import mock from '../../assets/images/Mock@2x.png'
import buttonPlayStore from '../../assets/images/playStore.png'
import playStore from '../../assets/images/playStoreQrCode.png'
import CustomInput from '../../components/CustomInput/customInput'
import AzureLoginButton from '../../components/AzureLoginButton/AzureLoginButton'
import * as api from '../../services/api'
import { request } from '../../util/handlerRequestUtil'
import { getMessage } from '../../util/IntlMessages'
import { isAzureEnabled, loginRequest } from '../../config/msal.config'
import './styles.css'

const Login = function ({ history }) {
  const isAuthenticated = useIsAuthenticated()
  const { instance, inProgress } = useMsal()
  const [isLoading, setIsLoading] = useState(false)
  const [isValidBayerUser, setIsValidBayerUser] = useState(true)
  const searchParams = new URLSearchParams(window.location.search)
  const parsed = Object.fromEntries(searchParams.entries())
  const dispatch = useDispatch()
  const activeAccount = instance.getActiveAccount()

  const handleClick = async () => {
    if (!isAzureEnabled) {
      console.warn('Azure login desabilitado: configuração ausente')
      return
    }

    instance
      .loginRedirect({
        ...loginRequest,
        prompt: 'login'
      })
      .catch((error) => console.log('loginRedirect error=======>', error))
  }

  useEffect(() => {
    if (parsed.expired) {
      dispatch(AlertActions.openAlert('Token expirou'))
    }
  }, [parsed])

  useEffect(() => {
    const processAzureAccount = async (account) => {
      try {
        if (isValidBayerUser) {
          const response = await request(api.authAzure(account.idToken), true, true)
          performLoginSuccess(response)
          return
        }
      } catch (error) {
        if (error.data.statusCode === 401) {
          setIsValidBayerUser(false)
          dispatch(AlertActions.openAlert('Usuário Bayer não atrelado ao soyshare'))
          setTimeout(() => {
            history.push('/logout')
          }, 3000)
          return
        }
        await getTokenRedirect(account)
      } finally {
        setIsLoading(false)
      }
    }

    if (activeAccount && !isLoading) {
      setIsLoading(true)
      processAzureAccount(activeAccount)
    }
  }, [activeAccount])

  const performLoginSuccess = async (response) => {
    if (response) {
      dispatch(
        authActions.success({
          ...response.user
        })
      )
      const appStorage = window.localStorage
      appStorage.setItem('userId', response.user && response.user.id)
      appStorage.setItem('entitlements', response.user && response.user.entitlements)
      history.push('/dashboard')
    }
  }

  const form = {
    initialValues: {},
    yupValidation: yup.object().shape({
      email: yup
        .string()
        .min(1, getMessage('minLenght', 'E-mail', '4'))
        .email(getMessage('isInvalidEmail'))
        .required(getMessage('isRequired', 'E-mail')),
      password: yup
        .string()
        .min(8, getMessage('minLenght', 'Senha', '8'))
        .required(getMessage('isRequired', 'Senha'))
    }),
    onSubmit: async (values) => {
      console.tron.log('submit values', values)
      try {
        const response = await request(
          api.auth({
            userLogin: values.email,
            userPassword: values.password
          })
        )
        performLoginSuccess(response)
      } catch (error) {
        console.tron.log('Auth error', error)
      }
    }
  }

  function renderButtons(submitForm) {
    return (
      <div className="containerFormActions">
        <Button
          type="link"
          className="buttonArea"
          onClick={() => {
            console.tron.log('history', history)
            history.push('password-recovery')
          }}
          style={{ padding: 0 }}
        >
          <span className="textColorWhite">
            <u>{getMessage('app.userAuth.forgotPassword')}</u>
          </span>
        </Button>
        <Button className="btEnter" type="primary" onClick={() => submitForm()}>
          {getMessage('app.userAuth.signIn')}
        </Button>
      </div>
    )
  }

  async function getTokenRedirect(account) {
    loginRequest.account = account
    return await instance.acquireTokenSilent(loginRequest).catch(async (error) => {
      console.log('silent token acquisition fails.')
      if (error instanceof InteractionRequiredAuthError) {
        // fallback to interaction when silent call fails
        console.log('acquiring token using redirect')
        instance.acquireTokenRedirect(loginRequest)
      } else {
        console.error(error)
      }
    })
  }

  const renderBody = () => {
    if (!isAzureEnabled) {
      return (
        <>
          <img src={logo} alt="Soyshare" className="logo" />
          {renderFormik()}
        </>
      )
    }

    if (isAuthenticated || inProgress === InteractionStatus.HandleRedirect) {
      return <Loading />
    }

    return (
      <>
        <img src={logo} alt="Soyshare" className="logo" />
        {renderFormik()}
        <AzureLoginButton text="Login com Azure" onClick={handleClick} />
      </>
    )
  }

  const Loading = () => (
    <div className="customFieldsList" style={{ backgroundColor: 'transparent' }}>
      <Icon type="loading" className="customFieldsList__loadingIcon" spin />
      <Typography className="customFieldsList__loadingText">{getMessage('loading...')}</Typography>
    </div>
  )

  function renderFormik() {
    return (
      <Formik
        enableReinitialize
        validationSchema={form.yupValidation}
        initialValues={form.initialValues}
        onSubmit={(values, { setSubmitting, setFieldError, resetForm }) =>
          form.onSubmit(values, setSubmitting, setFieldError, resetForm)
        }
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, errors, handleBlur, handleSubmit, setFieldValue, submitForm }) => {
          const handleKeyPress = (e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submitForm()
            }
          }

          return (
            <Form className="loginForm" layout="vertical" onSubmit={handleSubmit}>
              <Row>
                <Typography className="qrCodeText label">{getMessage('bayerUser')}</Typography>
                <CustomInput
                  customNameField="emptyString"
                  placeholder="E-mail"
                  field="email"
                  errors={errors}
                  setFieldValue={setFieldValue}
                  value={values}
                  handleBlur={handleBlur}
                  type="email"
                  onKeyPress={handleKeyPress}
                />
                <Typography className="qrCodeText label">{getMessage('bayerPassword')}</Typography>
                <CustomInput
                  placeholder="Senha"
                  type="password"
                  customNameField="emptyString"
                  field="password"
                  errors={errors}
                  setFieldValue={setFieldValue}
                  value={values}
                  handleBlur={handleBlur}
                  onKeyPress={handleKeyPress}
                />
              </Row>
              {renderButtons(submitForm)}
              {/* <Checkbox className="saveCredentials" value={saveCredential} onChange={() => setSaveCredentials(!saveCredential)} >{getMessage('credentials')}</Checkbox> */}
            </Form>
          )
        }}
      </Formik>
    )
  }

  return (
    <div className="mainlyContainer">
      <div className="container" style={{ height: -10 }}>
        <div className="coberturas1">
          <div className="mockContainer">
            <img src={mock} alt="Soyshare" className="mock" />
            <p className="desciptionText">
              O Soyshare também está <br /> disponível para dispositivos <br /> mobile
            </p>
          </div>
          <div>
            <p className="mockContainer qrCodeText" style={{ marginTop: '-10%' }}>
              Use o QR code para baixar
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
            <img src={playStore} alt="Soyshare" style={{ width: '20%' }} />
            <img src={buttonPlayStore} alt="Soyshare" style={{ marginTop: 2, width: '20%' }} />
          </div>
        </div>
        <div className="coberturas2">{renderBody()}</div>
      </div>
    </div>
  )
}

export default Login
